import { useMemo, useState } from 'react';
import { Box, Typography, Paper, Card, CardContent, Chip, CircularProgress, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import type { AppCardRelation, AppViewFilter, AppViewSort } from '@diggens/types/apps';
import { GripVertical, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNodes, useUpdateNode, usePicklist, usePicklists, useUsers } from '@diggens/shared-hooks';
import type { Node } from '@diggens/types';
import { NodeDrawer } from '../graph/NodeDrawer';
import { FixedFilterChips } from '../graph/FixedFilterChips';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { useSchema } from '../../contexts';
import { BOARD_FILTER_ANY, BOARD_FILTER_NONE, boardFilterToViewFilters, DATE_BOARD_PRESETS } from '../../features/views';

interface PipelineViewProps {
  /** A built-in module whose stage picklist PIPELINE_PICKLIST_MAP names; app boards pass picklistName instead. */
  moduleId?: string;
  nodeType: string;
  /** The picklist whose items are the columns; defaults to the module's. */
  picklistName?: string;
  /** The prop the column is read from and written to; defaults to `stage`. */
  stageField?: string;
  title?: string;
  /** A related record to show on each card and to filter the board by, such as a task's project. */
  cardRelation?: AppCardRelation;
  /** A number prop to total in each column header; app boards show no total unless their view names one. */
  sumField?: string;
  /** The app view's fixed filters: applied by the API, shown as read-only chips. */
  fixedFilters?: AppViewFilter[];
  /** Field keys of the type the reader may filter the board by, as controls above it (select, user and date fields). */
  boardFilters?: string[];
  /** The order cards take within a column. */
  defaultSort?: AppViewSort;
}

/** The built-in module pipelines total the deal-like `amount` prop; an app board totals only what its view declares. */
const MODULE_SUM_FIELD = 'amount';

interface IncludedRecord {
  id: string;
  type: string;
  props: Record<string, unknown>;
}

/** A board row: the node plus the related record the include returned, if any. */
type BoardNode = Node & { _includes?: Record<string, IncludedRecord | undefined> };

const RELATION_ALIAS = 'card_relation';
const ALL = '__all__';
const NONE = '__none__';

// Map moduleId to picklist name (built-in modules; app views carry their own picklistName)
const PIPELINE_PICKLIST_MAP: Record<string, string> = {
  sales: 'opportunity_stage',
  'pevc-dealflow': 'deal_stage',
  'pevc-fundraising': 'fundraising_stage',
};

export function PipelineView({ moduleId, nodeType, picklistName: picklistNameProp, stageField = 'stage', title, cardRelation, sumField: sumFieldProp, fixedFilters, boardFilters, defaultSort }: PipelineViewProps) {
  const sumField = sumFieldProp ?? (moduleId ? MODULE_SUM_FIELD : undefined);
  const navigate = useNavigate();
  const { getNodeDefinition, getEdgeDefinition, getListPath } = useSchema();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [relatedFilter, setRelatedFilter] = useState<string>(ALL);
  /** What the reader picked in each board filter control, by field key; absent means any. */
  const [boardFilterValues, setBoardFilterValues] = useState<Record<string, string>>({});

  const nodeConfig = getNodeDefinition(nodeType);
  /** Diggens keeps a read-only type's records: the board offers no create, and its cards do not drag. */
  const readOnly = nodeConfig?.readOnly === true;
  // The board filter controls and the fixed-filter chips need picklist items and user names; only boards with rules fetch them
  const hasRules = (!!boardFilters && boardFilters.length > 0) || (!!fixedFilters && fixedFilters.length > 0);
  const { data: allPicklists } = usePicklists({ enabled: hasRules });
  const { data: allUsers } = useUsers(undefined, { enabled: hasRules });
  const boardFilterFields = useMemo(() => (boardFilters ?? []).map((key) => nodeConfig?.formFields.find((field) => field.key === key)).filter((field): field is NonNullable<typeof field> => field !== undefined), [boardFilters, nodeConfig]);
  const picklistLabelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const picklist of allPicklists ?? []) {
      map[picklist.name] = Object.fromEntries((picklist.items as Array<{ id: string; label: string }>).map((item) => [item.id, item.label]));
    }
    return map;
  }, [allPicklists]);
  const userNameMap = useMemo(() => Object.fromEntries((allUsers ?? []).map((user) => [user.id, user.full_name || user.email])), [allUsers]);

  // Everything the API must filter by: the view's fixed filters plus what the reader picked above the board
  const viewFilters = useMemo(() => {
    const picked = boardFilterFields.flatMap((field) => boardFilterToViewFilters(field, boardFilterValues[field.key] ?? BOARD_FILTER_ANY));
    const all = [...(fixedFilters ?? []), ...picked];
    return all.length > 0 ? all : undefined;
  }, [fixedFilters, boardFilterFields, boardFilterValues]);

  const picklistName = picklistNameProp ?? (moduleId ? PIPELINE_PICKLIST_MAP[moduleId] : undefined) ?? '';
  const { data: picklist, isLoading: picklistLoading } = usePicklist(picklistName);
  const { data: nodesResponse, isLoading: nodesLoading } = useNodes(nodeType, {
    pageSize: 500,
    includes: cardRelation ? [{ edgeType: cardRelation.edgeType, direction: cardRelation.direction, nodeTypes: [], as: RELATION_ALIAS }] : undefined,
    viewFilters,
    defaultSort,
  });
  const updateNode = useUpdateNode();
  const allNodes = (nodesResponse?.data || []) as BoardNode[];
  const stages = (picklist?.items || []).sort((a, b) => a.order - b.order);

  // The related record each card shows, and the choices for the filter above the board
  const relatedOf = (node: BoardNode): IncludedRecord | undefined => (cardRelation ? node._includes?.[RELATION_ALIAS] : undefined);
  const relatedLabel = (record: IncludedRecord | undefined): string => {
    if (!record || !cardRelation) return '';
    const value = record.props[cardRelation.displayField] ?? record.props.name ?? record.props.title;
    return typeof value === 'string' && value !== '' ? value : 'Untitled';
  };
  const relationName = useMemo(() => {
    if (!cardRelation) return '';
    if (cardRelation.label) return cardRelation.label;
    const edge = getEdgeDefinition(cardRelation.edgeType);
    const targetType = edge ? (cardRelation.direction === 'outgoing' ? edge.targetTypes[0] : edge.sourceTypes[0]) : undefined;
    return (targetType && getNodeDefinition(targetType)?.displayName) || edge?.displayName || 'Related';
  }, [cardRelation, getEdgeDefinition, getNodeDefinition]);
  const relatedChoices = useMemo(() => {
    const choices = new Map<string, string>();
    for (const node of allNodes) {
      const record = relatedOf(node);
      if (record) choices.set(record.id, relatedLabel(record));
    }
    return [...choices.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    // relatedOf and relatedLabel close over cardRelation, which is in the deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNodes, cardRelation]);
  const nodes = useMemo(() => {
    if (!cardRelation || relatedFilter === ALL) return allNodes;
    if (relatedFilter === NONE) return allNodes.filter((node) => !relatedOf(node));
    return allNodes.filter((node) => relatedOf(node)?.id === relatedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNodes, cardRelation, relatedFilter]);

  const handleCardClick = (node: Node) => {
    // Navigate to detail page: the type's list page from the schema, else the plural map for built-ins without one
    const pluralMap: Record<string, string> = {
      Person: 'people',
      Opportunity: 'opportunities',
      FundraisingOpportunity: 'fundraising',
    };
    const listPath = getListPath(nodeType) ?? `/${pluralMap[nodeType] || `${nodeType.toLowerCase()}s`}`;
    navigate(`${listPath}/${node.id}`);
  };

  const getNodesForStage = (stageId: string): Node[] => {
    return nodes.filter((n) => n.props[stageField] === stageId);
  };

  const getStageValue = (stageId: string): number => {
    if (!sumField) return 0;
    return getNodesForStage(stageId).reduce((sum, n) => sum + ((n.props[sumField] as number) || 0), 0);
  };

  if (picklistLoading || nodesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stages.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No pipeline stages configured{picklistName ? ` in picklist ${picklistName}` : ''}</Typography>
      </Box>
    );
  }

  /** The choices one board filter control offers: picklist items or options, workspace users, or date presets. */
  const boardFilterChoices = (field: (typeof boardFilterFields)[number]): Array<{ value: string; label: string }> => {
    if (field.type === 'date' || field.type === 'datetime') return DATE_BOARD_PRESETS.map((preset) => ({ value: preset.value, label: preset.label }));
    if (field.type === 'user') return (allUsers ?? []).map((user) => ({ value: user.id, label: user.full_name || user.email })).sort((a, b) => a.label.localeCompare(b.label));
    if (field.picklistName) return Object.entries(picklistLabelMap[field.picklistName] ?? {}).map(([value, label]) => ({ value, label }));
    return (field.options ?? []).map((option) => ({ value: option, label: option }));
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h4">{title ?? `${nodeConfig?.displayName || nodeType} Pipeline`}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {boardFilterFields.map((field) => (
            <FormControl key={field.key} size="small" sx={{ minWidth: 180 }} data-testid={`board-filter-${field.key}`}>
              <InputLabel id={`board-filter-${field.key}`}>{field.label}</InputLabel>
              <Select
                labelId={`board-filter-${field.key}`}
                label={field.label}
                value={boardFilterValues[field.key] ?? BOARD_FILTER_ANY}
                onChange={(event) => setBoardFilterValues((current) => ({ ...current, [field.key]: event.target.value }))}
              >
                <MenuItem value={BOARD_FILTER_ANY}>Any</MenuItem>
                <MenuItem value={BOARD_FILTER_NONE}>No {field.label.toLowerCase()}</MenuItem>
                {boardFilterChoices(field).map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          {cardRelation && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="pipeline-related-filter">{relationName}</InputLabel>
              <Select labelId="pipeline-related-filter" label={relationName} value={relatedFilter} onChange={(event) => setRelatedFilter(event.target.value)}>
                <MenuItem value={ALL}>All</MenuItem>
                <MenuItem value={NONE}>No {relationName.toLowerCase()}</MenuItem>
                {relatedChoices.map(([id, label]) => (
                  <MenuItem key={id} value={id}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        {/* Diggens keeps a read-only type's records, so nobody creates one here */}
        {!readOnly && (
          <IconButton
            color="primary"
            onClick={() => {
              setSelectedNode(null);
              setDrawerOpen(true);
            }}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <Plus size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        )}
        </Box>
      </Box>

      {fixedFilters && fixedFilters.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FixedFilterChips filters={fixedFilters} sources={{ fields: nodeConfig?.formFields ?? [], picklistLabels: picklistLabelMap, userNames: userNameMap }} />
        </Box>
      )}

      {/* Kanban columns */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
        {stages.map((stage) => {
          const stageNodes = getNodesForStage(stage.id);
          const totalValue = getStageValue(stage.id);
          const bgColor = stage.is_closed ? (stage.is_won ? 'success.light' : 'error.light') : 'grey.100';

          const isDragOver = dragOverStage === stage.id;
          return (
            <Paper
              key={stage.id}
              sx={{
                minWidth: 280,
                maxWidth: 280,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: bgColor,
                border: isDragOver ? '2px dashed' : '2px solid transparent',
                borderColor: isDragOver ? 'primary.main' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverStage(stage.id);
              }}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                const nodeId = e.dataTransfer.getData('nodeId');
                const draggedNode = nodes.find((n) => n.id === nodeId);
                if (!readOnly && draggedNode && draggedNode.props[stageField] !== stage.id) {
                  updateNode.mutate({
                    id: draggedNode.id,
                    updates: { props: { ...draggedNode.props, [stageField]: stage.id } },
                    nodeType,
                  });
                }
              }}
            >
              <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {stage.label}
                  </Typography>
                  <Chip label={stageNodes.length} size="small" />
                </Box>
                {sumField && (
                  <Typography variant="caption" color="text.secondary">
                    ${totalValue.toLocaleString()}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, p: 1, overflowY: 'auto' }}>
                {stageNodes.map((node) => {
                  const name = (node.props.name as string) || (node.props.title as string) || 'Untitled';
                  const amount = sumField ? (node.props[sumField] as number | undefined) : undefined;
                  const related = relatedOf(node);
                  return (
                    <Card
                      key={node.id}
                      draggable={!readOnly}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('nodeId', node.id);
                      }}
                      onClick={() => handleCardClick(node)}
                      sx={{
                        mb: 1,
                        cursor: readOnly ? 'pointer' : 'grab',
                        '&:hover': { boxShadow: 3 },
                        '&:active': { cursor: readOnly ? 'pointer' : 'grabbing' },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {!readOnly && <GripVertical size={16} style={{ marginTop: 2, color: '#999' }} />}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {name}
                            </Typography>
                            {amount !== undefined && (
                              <Typography variant="caption" color="text.secondary">
                                ${amount.toLocaleString()}
                              </Typography>
                            )}
                            {cardRelation && (
                              <Typography variant="caption" color={related ? 'text.secondary' : 'text.disabled'} sx={{ display: 'block' }} noWrap>
                                {related ? `${relationName}: ${relatedLabel(related)}` : `No ${relationName.toLowerCase()}`}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {!readOnly && (
        <NodeDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedNode(null);
          }}
          nodeType={nodeType}
          mode={selectedNode ? 'edit' : 'create'}
          node={selectedNode || undefined}
        />
      )}
    </Box>
  );
}
